import React from "react";
import { groupBy, map } from "lodash";
import momentTz from "moment-timezone";
import { DateRange } from "@sonder/ui/source/styled/components/DateRangePicker";
import { SCHEDULER_ROUNDING } from "@sonder/ui/source/styled/components/ScheduleLayer/constants";
import {
  ScheduleLayoutWithNode,
  getEndAt,
  getStartAt,
} from "@sonder/ui/source/styled/components/ScheduleLayer/helpers";
import {
  CheckInCheckOutBlock,
  StackedSchedules,
} from "@sonder/ui/source/styled/fragments/ScheduleLayer";
import { SchedulerRow as SchedulerRowFragment } from "@sonder/ui/source/styled/layouts/SchedulerLayout";
import { ONE_HOUR, ONE_MINUTE } from "core/constants";
import { useCurrentUserTimezone } from "core/hooks/useCurrentTimeZone";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import {
  GetTaskSchedulesPlace,
  GetTaskSchedulesRelatedBooking,
  GetTaskSchedulesRelatedTask,
} from "core/queries/GetTaskSchedules";
import { useUpdateTask } from "core/queries/UpdateTask";
import { viewItem } from "schedule/actions";
import {
  InvisibleBlockProps,
  mapScheduleToInvisibleBlock,
} from "schedule/components/InvisibleBlock";
import {
  RelatedTaskBlock,
  RelatedTaskDecoyBlock,
} from "schedule/components/RelatedTaskBlock";
import { ScheduleBoundingLayer } from "schedule/components/ScheduleBoundingLayer";
import { ScheduleDecoyLayer } from "schedule/components/ScheduleDecoyLayer";
import { StackedScheduleLayer } from "schedule/components/StackedScheduleLayer";
import { roundTimeDown, roundTimeUp } from "schedule/helpers";
import { useStore } from "schedule/store";
import {
  CleaningRequestCategory,
  FilterStatus,
  TaskStatus,
} from "typings/globalGQLTypes";

interface SchedulerRowProps {
  place: GetTaskSchedulesPlace;
}

const matchesCleaningRequestCategory = (t: GetTaskSchedulesRelatedTask) =>
  t.cleaningRequest?.category &&
  [
    CleaningRequestCategory.intrastay,
    CleaningRequestCategory.re_clean,
    CleaningRequestCategory.space_clean,
    CleaningRequestCategory.stayover,
  ].includes(t.cleaningRequest?.category);

/**
 * intersectsBlockingScheduleItems
 * The majority of Scheduler assumes the FE and BE are in harmony, or at the least, the FE will poll to be updated.
 * However, with the introduction of SmartShuffling, there is a chance for the BE to update the FE in an unexpected way.
 * This in itself will be corrected momentarily, but it an async background job, and causes a strange bug.
 *
 * This code accounts for trusting the BE for the majority of logic, but attempts to preventatively correct the async
 * issues that might occur in general.
 *
 * Note: This function is just a little expensive. As long as we aren't rendering 4+weeks of data it should be fine.
 * We are comparing X scheduleItems to Y tasks. Usually the current max is 2 scheduleItems and 1 tasks.
 */
const intersectsBlockingScheduleItems = (
  scheduleItems: GetTaskSchedulesRelatedBooking[],
  task: GetTaskSchedulesRelatedTask,
  timezone: string,
) =>
  /**
   * Cases to Solve For:
   *
   * Key:
   * == Guest Stay
   * <| Guest Checking Out
   * |> Guest Checking In
   * {} Task Start and End At
   * [] Turnover Window
   *
   * Case A: Task DueAt after guest check in. (ECI)
   * [=========<|      {       |>====}====]
   *
   * Case B: Task ScheduledStartAt before guest check out. (ECO)
   * [===={====<|      }       |>=========]
   *
   * Case C: Task DueAt is after guest checkout AND Task ScheduledStartAt is before guest check in. (WTF)
   * [    {    |>==============<|    }    ]
   */
  scheduleItems.some(scheduleItem => {
    const scheduledItemEndTime = momentTz.tz(
      scheduleItem.checkOutTime,
      timezone,
    );
    const scheduledItemStartTime = momentTz.tz(
      scheduleItem.checkInTime,
      timezone,
    );
    const taskDueAt = momentTz.tz(task.dueAt, timezone);
    const taskScheduledStartAt = momentTz.tz(task.scheduledStartAt, timezone);
    return (
      // Case A
      taskDueAt.isBetween(scheduledItemStartTime, scheduledItemEndTime) ||
      // Case B
      taskScheduledStartAt.isBetween(
        scheduledItemStartTime,
        scheduledItemEndTime,
      ) ||
      // Case C
      (taskScheduledStartAt.isBefore(scheduledItemStartTime) &&
        taskDueAt.isAfter(scheduledItemEndTime))
    );
  });

export const SchedulerRow = ({ place }: SchedulerRowProps) => {
  const {
    dispatch,
    state: { dateRange, isDisabled, isSelecting },
  } = useStore();
  const userTimezone = useCurrentUserTimezone();
  const errorReporter = useErrorReporterFiltered();
  const [onUpdateTask] = useUpdateTask({
    refetchQueries: ["GetSchedulerMetrics"],
  });
  const relatedBookings = place?.relatedBookings || [];
  const relatedTasks = place?.relatedTasks || [];
  // HELPERS
  const mapScheduleToNode = (
    booking: GetTaskSchedulesRelatedBooking,
    timezone: string,
  ) => {
    const { id, checkOutTime, checkInTime } = booking;
    return {
      node: (
        <CheckInCheckOutBlock
          onClick={e => {
            if (isDisabled) return;
            const boundingRect = e.currentTarget.getBoundingClientRect();
            dispatch(
              viewItem([
                {
                  boundingRect,
                  booking,
                  place,
                },
              ]),
            );
          }}
        />
      ),
      endAt: checkOutTime
        ? roundTimeUp(momentTz.tz(checkOutTime, timezone))
        : null,
      i: id,
      isDraggable: false,
      isResizable: false,
      startAt: checkInTime
        ? roundTimeDown(momentTz.tz(checkInTime, timezone))
        : null,
    };
  };
  const mapTaskToNode = (task: GetTaskSchedulesRelatedTask) => {
    const {
      cleaningRequest,
      completedAt,
      dueAt,
      estimatedDuration,
      id,
      scheduledStartAt,
      startedAt,
      status,
    } = task;
    const shuffled = cleaningRequest?.shuffled || false;
    const isDraggableBasedOnStatus =
      status &&
      [FilterStatus.ready, FilterStatus.waiting].includes(
        status as FilterStatus,
      );
    const isResizableBasedOnStatus =
      status &&
      [
        FilterStatus.ready,
        FilterStatus.inprogress,
        FilterStatus.waiting,
      ].includes(status as FilterStatus);
    return {
      node: (
        <RelatedTaskBlock
          place={place}
          task={task as GetTaskSchedulesRelatedTask}
        />
      ),
      endAt: getEndAt({ startedAt, estimatedDuration, completedAt, dueAt }),
      i: id,
      isDraggable: !isDisabled && !isSelecting && isDraggableBasedOnStatus,
      isResizable: !isDisabled && !isSelecting && isResizableBasedOnStatus,
      shuffled,
      startAt: getStartAt({
        startedAt,
        dueAt,
        estimatedDuration,
        scheduledStartAt,
      }),
      status,
    };
  };
  const mapOccupiedBoundingLayers = (
    tasks: GetTaskSchedulesRelatedTask[],
    timezone: string,
  ) => {
    const newItems = tasks.reduce((memo, task) => {
      const { completedAt, earliestStartAt, latestEndAt, startedAt } = task;
      const isTaskInProgress = startedAt || completedAt;
      // Back out early if Task is in Progress
      if (isTaskInProgress) {
        return memo;
      }
      const momentEarliestStartAt = momentTz.tz(earliestStartAt, timezone);
      const momentLatestEndAt = momentTz.tz(latestEndAt, timezone);
      const isTaskStartInRange =
        momentEarliestStartAt.isSameOrAfter(dateRange.startAt) &&
        momentEarliestStartAt.isSameOrBefore(dateRange.endAt);
      const isTaskEndInRange =
        momentLatestEndAt.isSameOrAfter(dateRange.startAt) &&
        momentLatestEndAt.isSameOrBefore(dateRange.endAt);
      const isTaskInRange = isTaskStartInRange || isTaskEndInRange;
      if (!isTaskInRange) {
        return memo;
      }
      if (isTaskStartInRange) {
        memo.push({
          id: `${task.id}-pre_startAt`,
          startTime: dateRange.startAt,
          endTime: momentEarliestStartAt,
        });
      }
      if (isTaskEndInRange) {
        memo.push({
          id: `${task.id}-post_endAt`,
          startTime: momentLatestEndAt,
          endTime: dateRange.endAt,
        });
      }
      return memo;
    }, [] as InvisibleBlockProps[]);
    return [
      ...newItems.map(mapScheduleToInvisibleBlock),
      ...tasks.map(mapTaskToNode),
    ];
  };

  // BLOCKING SCHEDULES LOGIC
  // Note: `unoccupiedBlockingScheduledItems` means items that block tasks from being placed if they intersect with occupied schedules.
  // Confusing? Sorry. Give me better names.
  const unoccupiedBlockingScheduledItems = (relatedBookings || []).map(
    schedule =>
      mapScheduleToNode(
        schedule as GetTaskSchedulesRelatedBooking,
        userTimezone,
      ),
  );

  // ALL TASKS LOGIC
  const { unscheduledTasks = [], scheduledTasks = [] } = groupBy(
    relatedTasks,
    task =>
      !task.dueAt && !task.scheduledStartAt
        ? "unscheduledTasks"
        : "scheduledTasks",
  );

  // SCHEDULED TASKS LOGIC
  const { unoccupiedTasks = [], occupiedTasks = [] } = groupBy(
    scheduledTasks,
    t =>
      t.startedAt ||
      t.completedAt ||
      matchesCleaningRequestCategory(t) ||
      intersectsBlockingScheduleItems(relatedBookings || [], t, userTimezone)
        ? "occupiedTasks"
        : "unoccupiedTasks",
  );
  // These tasks cannot occur when a Guest is in a Unit / Space
  // We add the `unoccupiedBlockingScheduledItems` to prevent placement.
  const mappedUnoccupiedTasks = unoccupiedTasks.map(mapTaskToNode);
  const unoccupiedLayout = [
    ...unoccupiedBlockingScheduledItems,
    ...mappedUnoccupiedTasks,
  ];
  // If there is a multi day stay, there will be multiple tasks that need to be schedulable independent of one another.
  // We are relying on earliestStartAt / latestEndAt to save us from overlapping tasks.
  const occupiedLayers = map(groupBy(occupiedTasks, t => t.id));
  const occupiedBlockingScheduleLayers = occupiedLayers.map(item =>
    mapOccupiedBoundingLayers(item, userTimezone),
  );
  // UNSCHEDULED TASKS LOGIC
  const firstUnscheduledTask =
    unscheduledTasks && unscheduledTasks.length
      ? unscheduledTasks.shift()
      : null;
  const onDecoyPlace = async (newDateRange: DateRange) => {
    const taskId = firstUnscheduledTask && firstUnscheduledTask.id;
    if (!taskId) return;
    try {
      await onUpdateTask({
        variables: {
          taskId,
          newScheduledStartAt: newDateRange.startAt,
          dueBy: newDateRange.endAt,
        },
      });
    } catch (error) {
      errorReporter.notify(error);
    }
  };

  const isScheduleBoundingLayerDisabled = (
    task: GetTaskSchedulesRelatedTask,
  ) => {
    return task.status === TaskStatus.complete;
  };
  return (
    <SchedulerRowFragment>
      <StackedSchedules>
        {unoccupiedTasks.length > 0 &&
          unoccupiedTasks.map(task => {
            return (
              <ScheduleBoundingLayer
                disabled={isScheduleBoundingLayerDisabled(task)}
                endAt={roundTimeDown(
                  momentTz.tz(task.latestEndAt, userTimezone),
                )}
                key={task.id}
                maxEndAt={dateRange.endAt}
                maxStartAt={dateRange.startAt}
                startAt={roundTimeUp(
                  momentTz.tz(task.earliestStartAt, userTimezone),
                )}
                taskId={task.id}
              />
            );
          })}
        <StackedScheduleLayer
          layout={unoccupiedLayout as ScheduleLayoutWithNode[]}
        />
        {occupiedTasks.length > 0 &&
          occupiedTasks.map(task => {
            return (
              <ScheduleBoundingLayer
                disabled={isScheduleBoundingLayerDisabled(task)}
                endAt={roundTimeDown(
                  momentTz.tz(task.latestEndAt, userTimezone),
                )}
                key={task.id}
                maxEndAt={dateRange.endAt}
                maxStartAt={dateRange.startAt}
                startAt={roundTimeUp(
                  momentTz.tz(task.earliestStartAt, userTimezone),
                )}
                taskId={task.id}
              />
            );
          })}
        {occupiedBlockingScheduleLayers.length > 0 &&
          occupiedBlockingScheduleLayers.map(scheduledLayout => (
            <StackedScheduleLayer
              key={scheduledLayout[0].i}
              layout={scheduledLayout as ScheduleLayoutWithNode[]}
            />
          ))}
      </StackedSchedules>
      {firstUnscheduledTask && (
        <ScheduleDecoyLayer
          endAt={roundTimeDown(
            momentTz.tz(firstUnscheduledTask.latestEndAt, userTimezone),
          )}
          estimatedDuration={
            firstUnscheduledTask.estimatedDuration
              ? firstUnscheduledTask.estimatedDuration * ONE_MINUTE
              : ONE_HOUR
          }
          maxEndAt={dateRange.endAt}
          maxStartAt={dateRange.startAt}
          startAt={roundTimeUp(
            momentTz.tz(firstUnscheduledTask.earliestStartAt, userTimezone),
          )}
          onChange={onDecoyPlace}
        >
          <RelatedTaskDecoyBlock
            task={firstUnscheduledTask as GetTaskSchedulesRelatedTask}
            w={
              (firstUnscheduledTask.estimatedDuration
                ? firstUnscheduledTask.estimatedDuration * ONE_MINUTE
                : ONE_HOUR) /
              (SCHEDULER_ROUNDING * ONE_MINUTE)
            }
          />
        </ScheduleDecoyLayer>
      )}
    </SchedulerRowFragment>
  );
};
